import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';

export interface KitchenSettings {
  id?: string;
  company_id: string;
  
  // Preparation time settings (in minutes)
  default_prep_time: number;
  food_prep_time: number;
  beverage_prep_time: number;
  dessert_prep_time: number;
  
  // Alert thresholds (in minutes)
  warning_time: number;  // Yellow
  urgent_time: number;   // Orange
  critical_time: number; // Red
  
  // Notifications
  sound_enabled: boolean;
  auto_print_kitchen_ticket: boolean;
  
  created_at?: string;
  updated_at?: string;
}

export function useKitchenSettings() {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['kitchen-settings', company?.id],
    queryFn: async (): Promise<KitchenSettings | null> => {
      if (!company?.id) return null;

      const { data, error } = await supabase
        .from('kitchen_settings')
        .select('*')
        .eq('company_id', company.id)
        .single();

      if (error) {
        // If no settings exist, return defaults
        if (error.code === 'PGRST116') {
          return {
            company_id: company.id,
            default_prep_time: 15,
            food_prep_time: 20,
            beverage_prep_time: 5,
            dessert_prep_time: 10,
            warning_time: 10,
            urgent_time: 15,
            critical_time: 20,
            sound_enabled: true,
            auto_print_kitchen_ticket: false,
          };
        }
        throw error;
      }

      return data as KitchenSettings;
    },
    enabled: !!company?.id,
  });
}

export function useUpdateKitchenSettings() {
  const queryClient = useQueryClient();
  const { company } = useAuthStore();

  return useMutation({
    mutationFn: async (settings: Partial<KitchenSettings>) => {
      if (!company?.id) throw new Error('No company selected');

      const { data: existing } = await supabase
        .from('kitchen_settings')
        .select('id')
        .eq('company_id', company.id)
        .single();

      if (existing) {
        // Update existing
        const { data, error } = await supabase
          .from('kitchen_settings')
          .update(settings)
          .eq('company_id', company.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new
        const { data, error } = await supabase
          .from('kitchen_settings')
          .insert({
            company_id: company.id,
            ...settings,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-settings'] });
    },
  });
}
